import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

import { Modal, ModalField } from '@/components/Modal';
import { moment } from '@/lib/dates';
import {
  twoFactorMessage,
  useBeginTwoFactorSetup,
  useCancelTwoFactorSetup,
  useConfirmTwoFactorSetup,
  useDisableTwoFactor,
  useForgetTrustedDevices,
  useMyTwoFactor,
  useRegenerateRecoveryCodes,
} from '@/lib/two-factor';

/**
 * Two-factor authentication, on Settings › Security.
 *
 * The design draws one line — "Two-factor authentication is on ·
 * authenticator app" — and nothing for turning it on, which is where this
 * card's own screens come from, in the design's idiom: the same panel,
 * the same modal, the same small mono for the facts.
 *
 * ⚠️ **The secret is on screen exactly once**, in the enrolment modal, as a
 * QR and as text for a phone that cannot scan. Closing that modal without
 * confirming cancels the enrolment on the server, so nothing is left half
 * on. The recovery codes are shown once too, straight after, and the modal
 * refuses to close until "I have saved them" — the server holds only hashes
 * and cannot show them again; it can only make new ones.
 */

const SIX_DIGITS = /^\d{3}\s?\d{3}$/;

export function TwoFactorCard() {
  const status = useMyTwoFactor();
  const begin = useBeginTwoFactorSetup();
  const confirm = useConfirmTwoFactorSetup();
  const cancel = useCancelTwoFactorSetup();
  const disable = useDisableTwoFactor();
  const regenerate = useRegenerateRecoveryCodes();
  const forget = useForgetTrustedDevices();

  const [enrolling, setEnrolling] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [turningOff, setTurningOff] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const s = status.data;

  const startEnrolment = () =>
    begin.mutate(undefined, { onSuccess: (setup) => setEnrolling(setup) });

  return (
    <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
      <span className="flex flex-wrap items-center gap-2.5">
        <h2 className="eyebrow m-0">Two-factor authentication</h2>
        {s?.enabled ? (
          <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            since {s.enabledAt ? moment(s.enabledAt) : '—'}
          </span>
        ) : null}
        {!s?.enabled ? (
          <button
            type="button"
            disabled={!s || s.storageUnavailable || begin.isPending}
            onClick={startEnrolment}
            className="btn-small focus-ring ml-auto inline-flex cursor-pointer items-center gap-1 disabled:cursor-default disabled:opacity-50"
          >
            {begin.isPending ? 'Preparing…' : 'Turn on'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setTurningOff(true)}
            className="btn-small focus-ring ml-auto inline-flex cursor-pointer items-center gap-1"
          >
            Turn off
          </button>
        )}
      </span>

      {status.isError ? (
        <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
          {twoFactorMessage(status.error)}
        </span>
      ) : s?.enabled ? (
        <>
          {/* The design's line, word for word — true now that there is a factor. */}
          <span className="flex items-center gap-2 text-[11.5px]" style={{ color: 'var(--ok)' }}>
            <span
              aria-hidden="true"
              className="h-[5px] w-[5px] flex-none rounded-full"
              style={{ background: 'currentColor' }}
            />
            Two-factor authentication is on · authenticator app
          </span>
          <span
            className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px]"
            style={{ color: 'var(--muted)' }}
          >
            <span>
              Recovery codes · <span className="font-mono">{s.recoveryCodesLeft}</span> left
              <button
                type="button"
                onClick={() => setRegenerating(true)}
                className="btn-quiet focus-ring ml-2 cursor-pointer"
                style={{ color: 'var(--accent)' }}
              >
                Regenerate
              </button>
            </span>
            <span>
              Trusted devices · <span className="font-mono">{s.trustedDevices}</span>
              {s.trustedDevices > 0 ? (
                <button
                  type="button"
                  disabled={forget.isPending}
                  onClick={() => forget.mutate()}
                  className="btn-quiet focus-ring ml-2 cursor-pointer"
                  style={{ color: 'var(--accent)' }}
                >
                  Forget all
                </button>
              ) : null}
            </span>
          </span>
          {s.recoveryCodesLeft <= 2 ? (
            <span className="text-[11.5px]" style={{ color: 'var(--warn)' }}>
              {s.recoveryCodesLeft === 0
                ? 'No recovery codes left — regenerate a set before you need one.'
                : 'Only a couple of recovery codes left — regenerate a set soon.'}
            </span>
          ) : null}
        </>
      ) : (
        <span className="text-[11.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
          A code from an authenticator app is asked for after your password. Off: anybody with the
          password is in.
          {s?.storageUnavailable
            ? ' This server cannot store an authenticator secret yet — SECRETS_ENCRYPTION_KEY is not set.'
            : ''}
        </span>
      )}
      {begin.isError ? (
        <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
          {twoFactorMessage(begin.error)}
        </span>
      ) : null}

      {enrolling ? (
        <EnrolModal
          setup={enrolling}
          submitting={confirm.isPending}
          error={confirm.isError ? twoFactorMessage(confirm.error) : null}
          onConfirm={(code) =>
            confirm.mutate(code, {
              onSuccess: ({ recoveryCodes: codes }) => {
                setEnrolling(null);
                setRecoveryCodes(codes);
              },
            })
          }
          onClose={() => {
            setEnrolling(null);
            confirm.reset();
            cancel.mutate();
          }}
        />
      ) : null}

      {recoveryCodes ? (
        <RecoveryCodesModal codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
      ) : null}

      {turningOff ? (
        <TurnOffModal
          submitting={disable.isPending}
          error={disable.isError ? twoFactorMessage(disable.error) : null}
          onSubmit={(password, code) =>
            disable.mutate({ password, code }, { onSuccess: () => setTurningOff(false) })
          }
          onClose={() => {
            setTurningOff(false);
            disable.reset();
          }}
        />
      ) : null}

      {regenerating ? (
        <CodeModal
          title="New recovery codes"
          explanation="The codes you have now stop working the moment new ones exist. Enter the current code from your app to confirm."
          submitLabel="Regenerate"
          submitting={regenerate.isPending}
          error={regenerate.isError ? twoFactorMessage(regenerate.error) : null}
          onSubmit={(code) =>
            regenerate.mutate(code, {
              onSuccess: ({ recoveryCodes: codes }) => {
                setRegenerating(false);
                setRecoveryCodes(codes);
              },
            })
          }
          onClose={() => {
            setRegenerating(false);
            regenerate.reset();
          }}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────── modals ─────────────────────────────── */

function EnrolModal({
  setup,
  submitting,
  error,
  onConfirm,
  onClose,
}: {
  setup: { secret: string; otpauthUrl: string };
  submitting: boolean;
  error: string | null;
  onConfirm: (code: string) => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  const [svg, setSvg] = useState<string | null>(null);

  // Drawn in the browser from the URL; the server never sees a picture.
  useEffect(() => {
    let live = true;
    QRCode.toString(setup.otpauthUrl, { type: 'svg', margin: 1, width: 176 })
      .then((markup) => {
        if (live) setSvg(markup);
      })
      .catch(() => {
        if (live) setSvg(null);
      });
    return () => {
      live = false;
    };
  }, [setup.otpauthUrl]);

  return (
    <Modal
      title="Turn on two-factor authentication"
      submitLabel="Turn on"
      submitting={submitting}
      canSubmit={SIX_DIGITS.test(code.trim())}
      error={error}
      onSubmit={() => onConfirm(code.trim())}
      onClose={onClose}
      width={480}
    >
      <p className="m-0 text-[12.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
        Scan this with an authenticator app — 1Password, Google Authenticator, Authy, or the one
        built into your phone — then enter the six-digit code it shows.
      </p>
      <div className="flex flex-wrap items-start gap-4">
        <span
          className="flex h-[176px] w-[176px] flex-none items-center justify-center rounded-lg bg-white p-1"
          aria-label="QR code for the authenticator app"
          role="img"
          // The SVG is our own, built from a URL we composed; nothing untrusted is in it.
          dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
        >
          {svg ? undefined : (
            <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
              …
            </span>
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="field-label">Or enter this key by hand</span>
          <code
            className="font-mono text-[12px] leading-[1.6] break-all select-all"
            style={{ color: 'var(--text)' }}
          >
            {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
          </code>
          <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
            Time-based · SHA-1 · 6 digits · 30 seconds. Shown only now.
          </span>
        </span>
      </div>
      <ModalField label="Code from the app">
        <input
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123 456"
          onChange={(event) => setCode(event.target.value)}
          className="field field-mono w-full"
        />
      </ModalField>
    </Modal>
  );
}

function RecoveryCodesModal({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');

  return (
    <Modal
      title="Save your recovery codes"
      submitLabel="Done"
      canSubmit={saved}
      onSubmit={onDone}
      // Closing is the same as Done: the codes are gone from the screen either
      // way, and the checkbox is what asks somebody to mean it.
      onClose={saved ? onDone : () => undefined}
      width={440}
    >
      <p className="m-0 text-[12.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
        Each of these signs you in once if you lose the authenticator. They are shown only now — the
        server keeps them hashed and cannot show them again, only make new ones.
      </p>
      <ol
        className="m-0 grid list-none grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg border p-3 font-mono text-[13px]"
        style={{ borderColor: 'var(--line2)', background: 'var(--in)', color: 'var(--text)' }}
        aria-label="Recovery codes"
      >
        {codes.map((code) => (
          <li key={code} className="select-all">
            {code}
          </li>
        ))}
      </ol>
      <span className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true));
          }}
          className="btn-small focus-ring cursor-pointer"
        >
          {copied ? 'Copied' : 'Copy all'}
        </button>
      </span>
      <label className="flex items-start gap-2.5 text-[12.5px]" style={{ color: 'var(--text)' }}>
        <input
          type="checkbox"
          checked={saved}
          onChange={(event) => setSaved(event.target.checked)}
          className="mt-0.5 h-3.5 w-3.5"
          style={{ accentColor: 'var(--accent)' }}
        />
        I have saved these somewhere safe
      </label>
    </Modal>
  );
}

function TurnOffModal({
  submitting,
  error,
  onSubmit,
  onClose,
}: {
  submitting: boolean;
  error: string | null;
  onSubmit: (password: string, code: string) => void;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  return (
    <Modal
      title="Turn off two-factor authentication"
      submitLabel="Turn off"
      submitting={submitting}
      canSubmit={password !== '' && code.trim() !== ''}
      error={error}
      onSubmit={() => onSubmit(password, code.trim())}
      onClose={onClose}
    >
      <p className="m-0 text-[12.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
        Your password and a current code, because a signed-in session is exactly what the second
        factor exists to distrust. Every trusted device is forgotten with it.
      </p>
      <ModalField label="Password">
        <input
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
          className="field w-full"
        />
      </ModalField>
      <ModalField label="Code from the app, or a recovery code">
        <input
          value={code}
          autoComplete="one-time-code"
          onChange={(event) => setCode(event.target.value)}
          className="field field-mono w-full"
        />
      </ModalField>
    </Modal>
  );
}

function CodeModal({
  title,
  explanation,
  submitLabel,
  submitting,
  error,
  onSubmit,
  onClose,
}: {
  title: string;
  explanation: string;
  submitLabel: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (code: string) => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  return (
    <Modal
      title={title}
      submitLabel={submitLabel}
      submitting={submitting}
      canSubmit={code.trim() !== ''}
      error={error}
      onSubmit={() => onSubmit(code.trim())}
      onClose={onClose}
    >
      <p className="m-0 text-[12.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
        {explanation}
      </p>
      <ModalField label="Code from the app">
        <input
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123 456"
          onChange={(event) => setCode(event.target.value)}
          className="field field-mono w-full"
        />
      </ModalField>
    </Modal>
  );
}
